import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { AdminInput } from '../../components/admin/AdminUI';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { Trash2, Plus, Save } from 'lucide-react';
import { resolveImage } from '../../lib/images';

export const AdminGallery = () => {
  const { authedAxios } = useAdminAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // "Pending" upload area at the top
  const [pendingUrl, setPendingUrl] = useState('');
  const [pendingCaption, setPendingCaption] = useState('');
  const [pendingOrder, setPendingOrder] = useState('0');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/gallery');
      setImages(data.images || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const addImage = async () => {
    if (!pendingUrl) {
      alert('Please upload an image first.');
      return;
    }
    setSaving(true);
    try {
      await authedAxios().post('/api/admin/gallery', {
        url: pendingUrl,
        caption: pendingCaption,
        sort_order: parseInt(pendingOrder) || 0,
      });
      setPendingUrl('');
      setPendingCaption('');
      setPendingOrder('0');
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateImage = async (img, patch) => {
    try {
      await authedAxios().patch(`/api/admin/gallery/${img.id}`, patch);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  };

  const removeImage = async (img) => {
    if (!window.confirm('Delete this image? This cannot be undone.')) return;
    try {
      await authedAxios().delete(`/api/admin/gallery/${img.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <p className="text-[#F4F1EA]/60">Loading gallery...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-gallery-page" className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-[#F4F1EA]">Gallery</h2>
        <p className="text-[#F4F1EA]/60 mt-1">
          {images.length} photo{images.length === 1 ? '' : 's'} · Photos appear on the public Gallery page
        </p>
      </div>

      {/* Add new */}
      <div className="bg-[#0B132B] border border-[#B8860B]/30 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-bold text-[#B8860B] flex items-center gap-2">
          <Plus size={18} /> Add a Photo
        </h3>
        <ImageUploader
          testid="gallery-add-uploader"
          label="Upload"
          value={pendingUrl}
          onChange={setPendingUrl}
        />
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <AdminInput
              label="Caption (optional, shown on hover)"
              value={pendingCaption}
              onChange={(e) => setPendingCaption(e.target.value)}
              placeholder="e.g., Sunset over the deck"
            />
          </div>
          <AdminInput
            label="Sort order"
            type="number"
            value={pendingOrder}
            onChange={(e) => setPendingOrder(e.target.value)}
          />
        </div>
        <Button
          data-testid="admin-gallery-add-btn"
          onClick={addImage}
          disabled={saving || !pendingUrl}
          className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#F4F1EA]"
        >
          <Save className="mr-2 h-4 w-4" /> {saving ? 'Saving...' : 'Add to Gallery'}
        </Button>
      </div>

      {/* Existing */}
      <div>
        <h3 className="text-lg font-bold text-[#F4F1EA] mb-4">Existing Photos</h3>
        {images.length === 0 ? (
          <p className="text-[#F4F1EA]/60 bg-[#0B132B] border border-[#B8860B]/20 rounded-lg p-6 text-center">
            No photos yet. Add one above.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {images.map((img) => (
              <div
                key={img.id}
                data-testid={`admin-gallery-card-${img.id}`}
                className="bg-[#0B132B] border border-[#B8860B]/30 rounded-lg overflow-hidden"
              >
                <img
                  src={resolveImage(img.url)}
                  alt={img.caption || ''}
                  className="w-full h-48 object-cover bg-[#050814]"
                />
                <div className="p-4 space-y-3">
                  <AdminInput
                    label="Caption"
                    defaultValue={img.caption || ''}
                    onBlur={(e) =>
                      e.target.value !== (img.caption || '') &&
                      updateImage(img, { caption: e.target.value })
                    }
                  />
                  <div className="flex items-center gap-2">
                    <AdminInput
                      label="Order"
                      type="number"
                      defaultValue={String(img.sort_order ?? 0)}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value) || 0;
                        if (n !== (img.sort_order ?? 0)) updateImage(img, { sort_order: n });
                      }}
                    />
                    <button
                      data-testid={`admin-gallery-delete-${img.id}`}
                      onClick={() => removeImage(img)}
                      className="self-end mb-1 inline-flex items-center justify-center w-10 h-10 text-red-400 hover:bg-red-500/15 rounded border border-red-500/30"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
